<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher, onMount } from 'svelte';
  import type { Club } from '../types';
  import LogoSelector from './LogoSelector.svelte';
  import { actionFlags } from '../stores/actionFlags';

  const dispatch = createEventDispatcher();

  export let club: Partial<Club> | null = null; // null for new club, Partial<Club> for editing
  export let isEditMode: boolean = false;

  let dialogElement: HTMLDialogElement;
  let isAnimating = false;
  let showLogoSelector = false;
  let showDeleteConfirm = false;

  // @ts-ignore - Vite-specific type
  const BUILD_TIMESTAMP = import.meta.env.VITE_BUILD_TIMESTAMP || Date.now();

  // Form fields - initialize with club data if editing
  let name: string = club?.name || '';
  let sport: string = club?.sport || 'hockey';
  let location_name: string = club?.location_name || '';
  let location_address: string = club?.location_address || '';
  let location_zip_code: string = club?.location_zip_code || '';
  let location_city: string = club?.location_city || '';
  let logo_filename: string = club?.logo_filename || 'default.png';
  let logo_url: string = `/img/clublogos/${club?.logo_filename}?v=${BUILD_TIMESTAMP}` || '/img/clublogos/default.png';

  // Sports types
  const sportsTypes = [
    { label: $_('sports.field_hockey'), value: 'hockey' },
    { label: $_('sports.football'), value: 'football' }
  ];

  onMount(() => {
    if (dialogElement) {
      setTimeout(() => {
        dialogElement.classList.add('slide-up');
      }, 10);
    }
  });

  function onClose() {
    if (isAnimating) return;

    isAnimating = true;
    dialogElement.classList.remove('slide-up');
    dialogElement.classList.add('slide-down');

    setTimeout(() => {
      dispatch('cancel');
    }, 300);
  }

  function handleSubmit() {
    // Validate required fields
    if (!name.trim()) {
      return;
    }

    const clubData: Partial<Club> = {
      name: name.trim(),
      sport,
      location_name: location_name.trim(),
      location_address: location_address.trim(),
      location_zip_code: location_zip_code.trim(),
      location_city: location_city.trim(),
      logo_filename
    };

    if (isEditMode && club?.id) {
      dispatch('update', { clubId: club.id, clubData });
    } else {
      dispatch('create', { clubData });
    }
  }

  function handleLogoSelect(event: CustomEvent) {
    logo_url = event.detail.url;
    logo_filename = event.detail.key_path;
    showLogoSelector = false;
  }

  function handleLogoUpload(event: CustomEvent) {
    logo_url = event.detail.url;
    logo_filename = event.detail.key_path;
    showLogoSelector = false;
  }

  function openLogoSelector() {
    showLogoSelector = true;
  }

  function closeLogoSelector() {
    showLogoSelector = false;
  }

  function handleDeleteClick() {
    showDeleteConfirm = true;
  }

  function cancelDelete() {
    showDeleteConfirm = false;
  }

  function confirmDelete() {
    if (club?.id) {
      dispatch('delete', { clubId: club.id });
    }
  }
</script>

<dialog bind:this={dialogElement} open class="slide-up-dialog full-height">
  <article class="mt-0">
    <header class="py-4 mb-6">
      <h5>
        {isEditMode ? $_('clubSettings.editClub.title') : $_('clubSettings.newClub')}
      </h5>
      <button class="close-button" on:click={onClose}>×</button>
    </header>

    <form on:submit|preventDefault={handleSubmit} class="space-y-4">
      <!-- Logo Display and Selection -->
      <div class="logo-section">
        <label>{$_('clubSettings.form.logo')}</label>
        <div class="logo-display">
          <img
            src="{logo_url}?v={BUILD_TIMESTAMP}"
            alt={name || $_('clubSettings.form.clubLogo')}
            class="club-logo-preview"
          />
          <button
            type="button"
            class="small outline"
            on:click={openLogoSelector}
          >
            <ion-icon name="images-outline"></ion-icon>
            {$_('clubSettings.form.changeLogo')}
          </button>
        </div>
      </div>

      <!-- Sport Type (disabled when editing) -->
      <div>
        <label for="sport">{$_('signup.sports_type')}</label>
        <select
          id="sport"
          bind:value={sport}
          disabled={isEditMode}
          required
        >
          {#each sportsTypes as sportType}
            <option value={sportType.value}>{sportType.label}</option>
          {/each}
        </select>
      </div>

      <!-- Club Name -->
      <div>
        <label for="name">{$_('clubSettings.form.clubName')}</label>
        <input
          id="name"
          type="text"
          bind:value={name}
          placeholder={$_('clubSettings.form.clubNamePlaceholder')}
          required
          data-1p-ignore
        />
      </div>

      <!-- Location Name -->
      <div>
        <label for="location_name">{$_('clubSettings.form.locationName')}</label>
        <input
          id="location_name"
          type="text"
          bind:value={location_name}
          placeholder={$_('clubSettings.form.locationNamePlaceholder')}
        />
      </div>

      <!-- Location Address -->
      <div>
        <label for="location_address">{$_('clubSettings.form.locationAddress')}</label>
        <input
          id="location_address"
          type="text"
          bind:value={location_address}
          placeholder={$_('clubSettings.form.locationAddressPlaceholder')}
        />
      </div>

      <!-- Location Zip Code -->
      <div>
        <label for="location_zip_code">{$_('clubSettings.form.zipCode')}</label>
        <input
          id="location_zip_code"
          type="text"
          bind:value={location_zip_code}
          placeholder={$_('clubSettings.form.zipCodePlaceholder')}
        />
      </div>

      <!-- Location City -->
      <div>
        <label for="location_city">{$_('clubSettings.form.city')}</label>
        <input
          id="location_city"
          type="text"
          bind:value={location_city}
          placeholder={$_('clubSettings.form.cityPlaceholder')}
        />
      </div>

      <footer>
        <!-- Action Buttons -->
        <div class="footer-left">
          {#if isEditMode && !showDeleteConfirm}
            <button
              type="button"
              class="danger outline"
              on:click={handleDeleteClick}
              disabled={$actionFlags.isDeletingClub}
            >
              <ion-icon name="trash-outline"></ion-icon>
              {$_('common.delete')}
            </button>
          {/if}
        </div>
        <div class="footer-right">
          {#if showDeleteConfirm}
            <span class="delete-confirm-text">{$_('clubSettings.deleteConfirm')}</span>
            <button
              type="button"
              class="secondary outline"
              on:click={cancelDelete}
            >
              {$_('common.cancel')}
            </button>
            <button
              type="button"
              class="danger"
              on:click={confirmDelete}
              disabled={$actionFlags.isDeletingClub}
            >
              {#if $actionFlags.isDeletingClub}
                {$_('common.deleting')}...
              {:else}
                {$_('common.confirmDelete')}
              {/if}
            </button>
          {:else}
            <button
              type="button"
              class="secondary outline"
              on:click={onClose}
            >
              {$_('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={$actionFlags.isCreatingClub || $actionFlags.isUpdatingClub}
            >
              {#if $actionFlags.isCreatingClub || $actionFlags.isUpdatingClub}
                {$_('common.saving')}...
              {:else}
                {$_('common.save')}
              {/if}
            </button>
          {/if}
        </div>
      </footer>
    </form>
  </article>
</dialog>

<!-- Logo Selector Modal -->
<LogoSelector
  isOpen={showLogoSelector}
  currentLogo={logo_filename}
  on:selectLogo={handleLogoSelect}
  on:uploadLogo={handleLogoUpload}
  on:close={closeLogoSelector}
/>

<style>
  article {
    overflow-x: hidden;
  }

  .space-y-4 > * + * {
    margin-top: 1rem;
  }

  .logo-section {
    margin-bottom: 1.5rem;
  }

  .logo-display {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background-color: var(--pico-secondary-hover-background);
    border-radius: var(--pico-border-radius);
  }

  .club-logo-preview {
    width: 80px;
    height: 80px;
    object-fit: contain;
    border-radius: var(--pico-border-radius);
    background-color: white;
    padding: 0.5rem;
  }

  footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--pico-muted-border-color);
  }

  .footer-left {
    display: flex;
    gap: 0.5rem;
  }

  .footer-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
  }

  .delete-confirm-text {
    font-weight: 600;
    color: var(--pico-del-color);
    margin-right: 0.5rem;
  }

  button.danger {
    --pico-primary-background: var(--pico-del-color);
    --pico-primary-border: var(--pico-del-color);
    --pico-primary-hover-background: #d32f2f;
    --pico-primary-hover-border: #d32f2f;
  }

  button.danger.outline {
    --pico-primary-background: transparent;
    --pico-color: var(--pico-del-color);
    --pico-primary-border: var(--pico-del-color);
    --pico-primary-hover-background: var(--pico-del-color);
    --pico-primary-hover-border: var(--pico-del-color);
  }
</style>
